/* eslint-disable security/detect-unsafe-regex */
import { PathObject } from 'src/features/flows/lib/general';

export const chevronRight = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-chevron-right text-typography-grey shrink-0"><path d="m9 18 6-6-6-6"></path></svg>`;

export function isValidCurlyBraces(value: string) {
  // no curly braces OR matching curly braces (two opening followed by two closing braces)
  return !/[{}]/.test(value) || /^(?:[^{]*\{\{[^{}]*\}\}[^{]*)+$/.test(value);
}

/**
 * Splits a string on curly braces and returns an array of strings
 * @example
 * splitOnCurlyBraces("hello {{facet.path.to.variable}}") // returns ["hello ", "{{facet.path.to.variable}}"]
 * @param input - The string to split
 * @returns An array of strings
 */
export function splitOnCurlyBraces(input: string): string[] {
  if (!input || (!input.includes('{{') && !input.includes('}}'))) {
    return [input];
  }

  const result: string[] = [];
  let withinCurlyBraces = false;
  let nonBracePart = '';
  let bracePart = '';

  let i = 0;
  while (i < input.length) {
    // opening braces
    if (input[i] === '{') {
      // two consecutive opening braces
      if (input[i + 1] === '{') {
        // reset the brace part if we were already within curly braces
        if (withinCurlyBraces) {
          nonBracePart += bracePart;
          bracePart = input[i];
        }
        // entering a new curly braces part
        else {
          bracePart += input[i];
          withinCurlyBraces = true;
        }
      }
      // not consecutive opening braces
      else {
        if (withinCurlyBraces) {
          bracePart += input[i];
        } else {
          nonBracePart += input[i];
        }
      }
    }

    // closing braces
    else if (input[i] === '}') {
      // two consecutive closing braces
      if (input[i + 1] === '}') {
        // we found a closing brace part to an opening brace part
        if (withinCurlyBraces) {
          bracePart += '}}';
          if (nonBracePart) {
            result.push(nonBracePart);
          }
          result.push(bracePart);
          nonBracePart = '';
          bracePart = '';
          withinCurlyBraces = false;
          // skip the second closing brace
          i++;
        } else {
          nonBracePart += input[i];
        }
      } else {
        if (withinCurlyBraces) {
          bracePart += input[i];
        } else {
          nonBracePart += input[i];
        }
      }
    } else {
      if (withinCurlyBraces) {
        bracePart += input[i];
      } else {
        nonBracePart += input[i];
      }
    }

    i++;
  }
  if (nonBracePart || bracePart) {
    result.push(nonBracePart + bracePart);
  }

  return result;
}

export function isVariable(value: string) {
  return value?.startsWith('{{') && value?.endsWith('}}');
}
/**
 * Converts a curly brace variable string into an array of path segments
 * @example
 * parseCurlyBraceVariable("{{facet.path.to.variable}}") // returns ["facet", "path", "to", "variable"]
 * @param {string} value - The curly brace variable string to parse
 * @returns {string[]} Array of path segments
 */
export function parseCurlyBraceVariable(value: string): string[] {
  const splitOnDot = value.slice(2, -2).split('.');

  return splitOnDot.slice(splitOnDot.length > 1 ? 1 : 0);
}

export interface MatchResult {
  value: PathObject;
  location?: {
    start: number;
    end: number;
  };
}

export function fuzzyMatch(input: string, options: PathObject[]): MatchResult[] {
  if (!input || !options?.length) return [];
  if (input.length < 3) return [];

  // only match the last part of the input (after the last curly brace)
  const lowerInput = input.split('}}').slice(-1)[0].toLowerCase();

  let resultMap = new Map<number, MatchResult[]>();
  for (const option of options) {
    let lowerPathName = option.pathName.toLowerCase();

    // for facets, we use the displayPathname to match
    if (option.prefix === 'facet') {
      lowerPathName = option.displayPathname?.toLowerCase() || '';
    }

    let longestCommonSubstrLength = longestCommonTrailingSubstring(lowerInput.replaceAll(' ', '.'), lowerPathName);

    if (longestCommonSubstrLength > 2) {
      // if we found a longer match, reset the results
      if (!resultMap.has(longestCommonSubstrLength)) {
        resultMap.set(longestCommonSubstrLength, []);
      }
      resultMap.get(longestCommonSubstrLength)?.push({
        value: option,
        location: {
          end: input.length,
          start: input.length - longestCommonSubstrLength,
        },
      });
    }
  }

  const longestMatchLength = Math.max(...resultMap.keys());

  return resultMap.get(longestMatchLength) || [];
}

// returns the length of the longest common trailing substring
function longestCommonTrailingSubstring(input: string, option: string) {
  // omit the loop if the input is a substring of the option
  if (option.includes(input)) {
    return input.length;
  }

  // match the longest trailing substring by iterating over the input with decreasing slice length
  for (let i = 0; i < input.length - 2; i++) {
    const inputSubstr = input.slice(i);

    // return as soon as we find a match because we know it's the longest
    if (option.includes(inputSubstr)) {
      // if the match starts with a dot, we need to return the length minus 1 to remove the prefix
      if (inputSubstr[0] === '.') {
        return inputSubstr.length - 1;
      }
      return inputSubstr.length;
    }
  }

  return 0;
}
